import * as Sentry from "@sentry/react";

/**
 * Sends an exception to Sentry with optional tags and context.
 *
 * @param {Object} options - Options for reporting to Sentry.
 * @param {any} options.error - The error or exception to report (required).
 * @param {string} options.contextName - Context name for Sentry context (required).
 * @param {Record<string, any>} [options.context] - Additional context to attach (will be set as a Sentry context object).
 * @param {object} [options.tags] - Optional tags to set (key-value pairs).
 */
export function sendExceptionToSentry({
    error,
    contextName,
    context,
    tags
}: {
    error: any,
    contextName: string,
    context?: Record<string, any>,
    tags?: Record<string, any>
}) {
    Sentry.withScope(scope => {
        if (tags) {
            Object.entries(tags).forEach(([key, value]) => {
                scope.setTag(key, value);
            });
        }
        if (context && contextName) {
            scope.setContext(contextName, context);
        }
        Sentry.captureException(error);
    });
}

interface SentryUser {
    uid: string;
    email: string;
}

export function setSentryUser(user: SentryUser) {
    try {
        if (user) {
            Sentry.setContext("user", {
                id: user.uid,
                email: user.email,
            });
            Sentry.setTag("user.id", user.uid);
            Sentry.setTag("user.email", user.email);
        }
    } catch (error) {
        console.error('Error setting Sentry context', error);
    }
}

export function clearSentryUser() {
    try {
        Sentry.setUser(null);
        Sentry.setContext("user", null);
        Sentry.setTag("user.id", null);
        Sentry.setTag("user.email", null);
    } catch (error) {
        console.error('Error clearing Sentry user', error);
    }
}
